import isAnyArray from 'is-any-array';
import max from 'ml-array-max';
import min from 'ml-array-min';
import { xIsMonotone } from 'ml-spectra-processing';
import { getNormalizedSpectrum } from './util/getNormalizedSpectrum';
import { getXYSpectrum } from './util/getXYSpectrum';
/**
 * Class allowing to store and manipulate an analysis.
 * An analysis may contain one or more spectra that can be selected
 * based on their units
 */
export class Analysis {
    constructor(options = {}) {
        this.id = options.id || Math.random().toString(36).substring(2, 10);
        this.label = options.label || this.id;
        this.spectra = [];
        this.cache = {};
    }
    /**
     * Add a spectrum in the internal spectra variable
     */
    pushSpectrum(variables, options = {}) {
        this.spectra.push(standardizeData(variables, options));
        this.cache = {};
    }
    /**
     * Retrieve a Spectrum based on x/y units
     * @param {object} [selector={}]
     * @param {string} [selector.units] Units separated by vs like for example "g vs °C"
     * @param {string} [selector.xUnits] if undefined takes the first variable
     * @param {string} [selector.yUnits] if undefined takes the second variable
     * @returns {Spectrum}
     */
    getXYSpectrum(selector = {}) {
        let id = JSON.stringify(selector);
        if (!this.cache[id]) {
            this.cache[id] = getXYSpectrum(this.spectra, selector);
        }
        return this.cache[id];
    }
    /**
     * Retrieve a xy object
     * @param {object} [selector={}]
     * @param {string} [selector.units] Units separated by vs like for example "g vs °C"
     * @param {string} [selector.xUnits] if undefined takes the first variable
     * @param {string} [selector.yUnits] if undefined takes the second variable
     * @returns {Spectrum}
     */
    getXY(selector = {}) {
        let spectrum = this.getXYSpectrum(selector);
        if (!spectrum)
            return undefined;
        return {
            x: spectrum.variables.x.data,
            y: spectrum.variables.y.data,
        };
    }
    /**
     * Return the data object for specific x/y units with possibly some
     * normalization options
     * @param {object} [options={}]
     * @param {object} [options.selector]
     * @param {string} [options.selector.xUnits] // if undefined takes the first variable
     * @param {string} [options.selector.yUnits] // if undefined takes the second variable
     * @param {object} [options.normalization]
     *
     */
    getNormalizedSpectrum(options = {}) {
        const { normalization, selector } = options;
        const spectrum = this.getXYSpectrum(selector);
        if (!spectrum)
            return undefined;
        return getNormalizedSpectrum(spectrum, normalization);
    }
    /**
     * Returns the xLabel
     * @param {object} [selector]
     * @param {string} [selector.xUnits] // if undefined takes the first variable
     * @param {string} [selector.yUnits] // if undefined takes the second variable
     * @returns {string}
     */
    getXLabel(selector) {
        var _a;
        return (_a = this.getXYSpectrum(selector)) === null || _a === void 0 ? void 0 : _a.variables.x.label;
    }
    /**
     * Returns the yLabel
     * @param {object} [selector]
     * @param {string} [selector.xUnits] // if undefined takes the first variable
     * @param {string} [selector.yUnits] // if undefined takes the second variable
     * @returns {string}
     */
    getYLabel(selector) {
        var _a;
        return (_a = this.getXYSpectrum(selector)) === null || _a === void 0 ? void 0 : _a.variables.y.label;
    }
}
/**
 * Internal function that ensure the order of x / y array
 */
function standardizeData(variables, options = {}) {
    let { meta = {}, tmp = {}, dataType = '', title = '' } = options;
    let xVariable = variables.x;
    let yVariable = variables.y;
    if (!xVariable || !yVariable) {
        throw Error('A spectrum must contain at least x and y variables');
    }
    if (!isAnyArray(xVariable.data) || !isAnyArray(yVariable.data)) {
        throw Error('x and y variables must contain an array data');
    }
    let x = xVariable.data;
    let reverse = x && x.length > 1 && x[0] > x[x.length - 1];
    for (let key in variables) {
        let variable = variables[key];
        if (reverse)
            variable.data = variable.data.reverse();
        variable.label = variable.label || key;
        variable.units =
            variable.units ||
                variable.label.replace(/^.*[([](?<units>.*)[)\]].*$/, '$<units>');
        variable.min = min(variable.data);
        variable.max = max(variable.data);
        variable.isMonotone = xIsMonotone(variable.data);
    }
    return {
        variables,
        title,
        dataType,
        meta,
        tmp,
    };
}
//# sourceMappingURL=data:application/json;base64,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